<script setup>
import {computed, defineEmits, defineProps, onMounted, ref, nextTick} from 'vue'
import {storeToRefs} from 'pinia'
import {useAuthenticationStore, useDefaultStore} from '@/stores'
import BaseForm from '@shared-components/base/BaseForm.vue'
import {Modal as BaseModal} from 'bootstrap'
import TheSelect from '@shared-components/ui/selects/TheSelect.vue'
import InputPreloader from '@shared-components/ui/inputs/InputPreloader.vue'
import {useCompanyStore} from '@/views/companies/CompanyStore'
import TheSelectAdAccounts from '@shared-components/ui/selects/TheSelectAdAccounts.vue'
import {useSharePermissionsRequestStore} from '@/views/ad-account-requests/share-permissions/SharePermissionsRequestStore'
import ApiErrors from '@shared-components/ui/ApiErrors.vue'
import TheSelectBusinessManagers from '@/views/assets/business-managers/components/TheSelectBusinessManagers.vue'
import TheSelectEmails from '@shared-components/ui/selects/TheSelectEmails.vue'
import BaseCheckbox from '@shared-components/base/BaseCheckbox.vue'
import {formatPlatformToLowercase} from '@shared/utils/helpers'
import {useAdPlatformStore} from '@/stores/AdPlatformStore'
import AlertOffPeakWarning from '@shared-components/ui/alerts/AlertOffPeakWarning.vue'

// Emits & props
const emit = defineEmits(['created', 'hidden'])
const props = defineProps({
  id: {
    type: String,
    default: null,
  },
})

const modalId = props.id ?? 'share-request-modal'
const modal = ref()
const error = ref()
const selectAdAccount = ref()
const selectBusinessManagers = ref()
const {userCompanyHash, isCoreTeam} = storeToRefs(useAuthenticationStore())
const {createSharePermissionsRequest} = useSharePermissionsRequestStore()
const {companies, companiesLoading} = storeToRefs(useCompanyStore())
const {showOffPeakHoursAlert} = storeToRefs(useDefaultStore())

const {getPlatforms} = useAdPlatformStore()
const {platformOptionsByService, loading: platformsLoading} = storeToRefs(useAdPlatformStore())

const loading = ref(false)
const accessDurationOptions = [
  {value: 1, label: '1 day'},
  {value: 3, label: '3 days'},
  {
    value: 5,
    label: '5 days',
  },
]

const defaultFormData = {
  company_hash: userCompanyHash.value ?? '',
  platform: '',
  type: 'ad_account',
  grant_to: [],
  revoke_from: [],
  business_manager_hashes: [],
  ad_account_ids: [],
  admin_access: undefined,
  term: undefined,
}
const formData = ref(cloneSafe(defaultFormData))

const platformOptions = computed(() => {
  const list = platformOptionsByService.value['ad_account_request'] || []
  return list.map(item => ({
    label: item.label,
    value: formatPlatformToLowercase(item.name),
  }))
})

// Computed
const companiesOptions = computed(() =>
  companies.value.map(company => ({
    value: company.hash,
    label: company.title,
  })),
)
const showAdminAccessCheckbox = computed(() => ['tiktok', 'snapchat', 'google'].includes(formData.value.platform))

// Methods
onMounted(() => {
  let modalElement = document.getElementById(modalId)
  modal.value = new BaseModal(modalElement)

  modalElement.addEventListener('hidden.bs.modal', () => {
    emit('hidden')
  })

  getPlatforms()
  openModal()
})

const openModal = () => {
  modal.value.show()
}

const closeModal = () => {
  modal.value.hide()
}

const onCompanyChange = () => {
  selectBusinessManagers.value?.handleSearch('  ')

  onPlatformChanged(formData.value.platform)
}

const onPlatformChanged = value => {
  if (value === null) {
    return
  }

  if (!['meta', 'bing'].includes(value) && formData.value.type === 'business_manager') {
    formData.value.type = 'ad_account'
  }

  nextTick(() => {
    selectBusinessManagers.value?.handleSearch('  ')
    selectAdAccount.value?.handleSearch('  ')
  })

  formData.value.ad_account_ids.length = 0
  formData.value.business_manager_hashes.length = 0
}

const changeType = value => {
  formData.value.type = value

  if (value === 'business_manager') {
    formData.value.ad_account_ids.length = 0
    return
  }
  if (value === 'ad_account') {
    formData.value.business_manager_hashes.length = 0
  }

  nextTick(() => {
    selectBusinessManagers.value?.handleSearch('  ')
    selectAdAccount.value?.handleSearch('  ')
  })
}

const handleCreate = async () => {
  loading.value = true
  let payload = cloneSafe(formData.value)

  if (payload.platform === 'meta') {
    payload.platform = 'facebook'
  }

  try {
    await createSharePermissionsRequest(payload)
  } catch (responseError) {
    error.value = responseError?.data
    return false
  } finally {
    loading.value = false
  }

  closeModal()
  emit('created')
  formData.value = cloneSafe(defaultFormData)
}
</script>

<template>
  <div class="modal fade" :id="modalId" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Share Access Request</h5>
          <button type="button" class="btn-close" aria-label="Close" @click="closeModal" />
        </div>

        <base-form @on-submitted="handleCreate">
          <div class="modal-body">
            <alert-off-peak-warning v-if="showOffPeakHoursAlert" class="custom mb-4" />

            <div v-if="isCoreTeam" class="form-group">
              <label>Company *</label>
              <input-preloader :show-preloader="companiesLoading && !formData.company_hash" name="company">
                <the-select
                  id="company"
                  name="company_hash"
                  placeholder="Select a Company"
                  :options="companiesOptions"
                  v-model="formData.company_hash"
                  @change="onCompanyChange"
                  :can_deselect="false"
                  :allow_clear="false"
                  required
                />
              </input-preloader>
            </div>

            <div class="form-group">
              <label>Platform *</label>
              <input-preloader :show-preloader="platformsLoading" name="platform">
                <the-select
                  id="platform"
                  name="platform"
                  @change="onPlatformChanged"
                  placeholder="Select a Platform"
                  :options="platformOptions"
                  v-model="formData.platform"
                  :allow_clear="false"
                  :can_deselect="false"
                  required
                />
              </input-preloader>
            </div>
            <div v-if="['meta', 'bing'].includes(formData.platform)" class="form-group">
              <div class="btn-group btn-row-group" role="group">
                <button
                  @click="changeType('ad_account')"
                  :class="['btn btn-secondary', formData.type === 'ad_account' ? 'active' : '']"
                  type="button"
                >
                  Ad Account
                </button>
                <button
                  @click="changeType('business_manager')"
                  :class="['btn btn-secondary', formData.type === 'business_manager' ? 'active' : '']"
                  type="button"
                >
                  Business Manager
                </button>
              </div>
            </div>
            <div v-if="formData.type === 'ad_account'" class="form-group">
              <label>Ad Account *</label>
              <the-select-ad-accounts
                v-model="formData.ad_account_ids"
                :list-by-search="!!userCompanyHash"
                :payload="{
                  platform: formData.platform,
                  company_hash: formData.company_hash,
                }"
                :multiple="true"
                :disabled="formData.company_hash === undefined || !formData.platform"
                ref="selectAdAccount"
                required
              ></the-select-ad-accounts>
            </div>
            <div
              v-if="formData.type === 'business_manager' && ['meta', 'bing'].includes(formData.platform)"
              class="form-group"
            >
              <label>Business Manager *</label>
              <the-select-business-managers
                v-model="formData.business_manager_hashes"
                :list-by-search="!!userCompanyHash"
                :platform="formData.platform"
                :payload="{
                  company_hash: formData.company_hash,
                }"
                :disabled="formData.company_hash === undefined || !formData.platform"
                :with-appended="false"
                mode="tags"
                ref="selectBusinessManagers"
                required
              >
              </the-select-business-managers>
            </div>
            <div class="form-group">
              <label>Grant Access To</label>
              <the-select-emails
                v-model="formData.grant_to"
                placeholder="Enter Email to Grant Access"
                :required="formData.revoke_from.length === 0"
              />
            </div>
            <div class="form-group">
              <label>Revoke Access From</label>
              <the-select-emails
                v-model="formData.revoke_from"
                placeholder="Enter Email to Revoke Access"
                :required="formData.grant_to.length === 0"
              />
            </div>
            <div class="form-group">
              <label>Comment</label>
              <textarea v-model="formData.comment" class="form-control" rows="2"></textarea>
            </div>
            <template v-if="showAdminAccessCheckbox">
              <div class="form-group">
                <base-checkbox v-model="formData.admin_access" title="Admin Access" id="share-request-admin_access" />
              </div>
              <div v-if="formData.admin_access" class="form-group">
                <label>Access Duration</label>
                <the-select
                  v-model="formData.term"
                  :options="accessDurationOptions"
                  :allow_clear="false"
                  :can_deselect="false"
                  required
                />
              </div>
            </template>
            <div v-if="error" class="mt-3">
              <api-errors :error="error"></api-errors>
            </div>
          </div>
          <div class="modal-footer justify-content-end custom gap-4">
            <button type="button" class="btn btn-secondary" :disabled="loading" @click="closeModal">Close</button>
            <div>
              <button type="submit" class="btn btn-primary" :disabled="loading">Submit</button>
            </div>
          </div>
        </base-form>
      </div>
    </div>
  </div>
</template>
